import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface ISegmentEntityAttributes extends IModelAttributes {
	durationInMinutes: number;

	endStopId: string;
	endStop: Models.StopEntity | Models.IStopEntityAttributes;
	routeId: string;
	route: Models.RouteEntity | Models.IRouteEntityAttributes;
	startStopId: string;
	startStop: Models.StopEntity | Models.IStopEntityAttributes;
}
@entity('SegmentEntity', 'Segment')
export default class SegmentEntity extends Model implements ISegmentEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<SegmentEntity, number>()
	public durationInMinutes: number;

	@observable
	@attribute()
	public endStopId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public endStop: Models.StopEntity;

	@observable
	@attribute()
	public routeId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public route: Models.RouteEntity;

	@observable
	@attribute()
	public startStopId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public startStop: Models.StopEntity;

	constructor(attributes?: Partial<ISegmentEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<ISegmentEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.durationInMinutes !== undefined) {
				this.durationInMinutes = attributes.durationInMinutes;
			}
			if (attributes.endStopId !== undefined) {
				this.endStopId = attributes.endStopId;
			}
			if (attributes.endStop !== undefined) {
				if (attributes.endStop === null) {
					this.endStop = attributes.endStop;
				} else if (attributes.endStop instanceof Models.StopEntity) {
					this.endStop = attributes.endStop;
					this.endStopId = attributes.endStop.id;
				} else {
					this.endStop = new Models.StopEntity(attributes.endStop);
					this.endStopId = this.endStop.id;
				}
			}
			if (attributes.routeId !== undefined) {
				this.routeId = attributes.routeId;
			}
			if (attributes.route !== undefined) {
				if (attributes.route === null) {
					this.route = attributes.route;
				} else if (attributes.route instanceof Models.RouteEntity) {
					this.route = attributes.route;
					this.routeId = attributes.route.id;
				} else {
					this.route = new Models.RouteEntity(attributes.route);
					this.routeId = this.route.id;
				}
			}
			if (attributes.startStopId !== undefined) {
				this.startStopId = attributes.startStopId;
			}
			if (attributes.startStop !== undefined) {
				if (attributes.startStop === null) {
					this.startStop = attributes.startStop;
				} else if (attributes.startStop instanceof Models.StopEntity) {
					this.startStop = attributes.startStop;
					this.startStopId = attributes.startStop.id;
				} else {
					this.startStop = new Models.StopEntity(attributes.startStop);
					this.startStopId = this.startStop.id;
				}
			}
		}
	}
}
